"use client"

import { useState, useEffect } from "react"
import Link from "next/link"
import Image from "next/image"
import { ChevronDown, ChevronUp, ChevronRight, MapPin, Box, Settings, Check, Tag, ArrowRight } from "lucide-react"
import FaqLink from "@/components/faq/faq-link"

export default function FAQPage() {
  const [openItemId, setOpenItemId] = useState<string | null>("faq-1")
  const [activeCategory, setActiveCategory] = useState<string>("all")
  
  // Handle URL hash for direct linking to FAQ items
  useEffect(() => {
    // Check if there's a hash in the URL and open that FAQ item
    if (typeof window !== "undefined") {
      const hash = window.location.hash;
      if (hash) {
        const id = hash.replace('#', '');
        // Find the FAQ item with this ID or slug
        const found = faqItems.find(item => 
          item.id === id || 
          item.slug === id ||
          `faq-${item.slug}` === id
        );
        
        if (found) {
          setOpenItemId(found.id);
          // Scroll to the element after a short delay
          setTimeout(() => {
            const element = document.getElementById(found.id);
            if (element) {
              element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          }, 100);
        }
      }
    }
  }, []);

  // Function to navigate to a specific FAQ item
  const navigateToFaq = (id: string) => {
    // Find the FAQ item
    const item = faqItems.find(item => item.id === id);
    if (!item) return;
    
    // Set category if needed
    if (item.category) {
      setActiveCategory(item.category);
    }
    
    // Open the FAQ item
    setOpenItemId(id);
    
    // Update URL hash
    if (typeof window !== "undefined") {
      history.pushState(null, '', `#${item.slug || item.id}`);
    }
    
    // Scroll to the FAQ section first
    const faqSection = document.getElementById("faq");
    if (faqSection) {
      faqSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      
      // Then scroll to the specific item (with a slight delay to ensure proper rendering)
      setTimeout(() => {
        // Try again after DOM has updated with any category filtering
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 300);
    }
  };
  
  // Toggle FAQ item open/closed
  const toggleFaqItem = (id: string) => {
    // If it's already open, close it
    if (openItemId === id) {
      setOpenItemId(null);
      if (typeof window !== "undefined") {
        history.pushState(null, '', window.location.pathname);
      }
      return;
    }
    
    // Otherwise, navigate to it
    navigateToFaq(id);
  };
  
  // Handle category filtering
  const filterByCategory = (category: string) => {
    setActiveCategory(category);
  };

  // FAQ Data organized by category
  const faqItems = [
    {
      id: "faq-1",
      slug: "missing-item",
      question: "I'm missing an item from my order",
      answer: "We are truly sorry this happened and we will make it up to you! Please contact info@716cannabisllc.com with your order number and the item(s) that are missing so we can fix the problem ASAP.",
      category: "order-issues",
      relatedQuestions: ["faq-2", "faq-6"] // Links to related questions by ID
    },
    {
      id: "faq-2",
      slug: "wrong-item",
      question: "I've received an incorrect item in my order",
      answer: "We are sorry sometimes mistakes can happen, we are only human. Please visit the store you purchased the product from and we will exchange it for the correct item as long as it has not already been opened.",
      category: "order-issues",
      relatedQuestions: ["faq-1", "faq-6"]
    },
    {
      id: "faq-3",
      slug: "cancel-order",
      question: "Can I cancel my order?",
      answer: "Please email info@716cannabisllc.com as soon as possible. There's a chance your order may not have left our store and we can cancel it for you. If the order has already left our facility we will not be able to cancel your order.",
      category: "order-issues",
      relatedQuestions: ["faq-6", "faq-9"]
    },
    {
      id: "faq-4",
      slug: "restock-items",
      question: "Will an item be back in stock?",
      answer: "We can't always guarantee when a product will be back in stock but please email info@716cannabisllc.com for specific stock information for a product of your choice.",
      category: "product-info",
      relatedQuestions: ["faq-8"]
    },
    {
      id: "faq-5",
      slug: "opening-hours",
      question: "What are your opening hours?",
      answer: "Our store is open for in-store pickup during the following hours:<br>Mon - Sat: 8am - 10pm<br>Sun: 12pm - 9pm<br><br>Delivery is available during the following hours:<br>Mon - Sat:  11am - 7pm<br>Sun: 11am - 3:30pm",
      category: "store-info",
      relatedQuestions: ["faq-7"]
    },
    {
      id: "faq-6",
      slug: "returns-exchanges",
      question: "Can I return/exchange my order?",
      answer: "Please email info@716cannabisllc.com as soon as possible or come back into the store. We will exchange with a receipt any defective product for the same/like product. Flower is not returnable.",
      category: "order-issues",
      relatedQuestions: ["faq-1", "faq-2"]
    },
    {
      id: "faq-7",
      slug: "delivery-options",
      question: "Do you offer delivery?",
      answer: "Yes! Delivery is the same day within 2 hrs or less.<br><br>Our delivery hours are:<br>Mon - Sat:  11am - 7pm<br>Sun: 11am - 3:30pm",
      category: "delivery",
      relatedQuestions: ["faq-9", "faq-5"]
    },
    {
      id: "faq-8",
      slug: "cannabis-source",
      question: "Where does your cannabis come from?",
      answer: "Our cannabis is grown all over the country! Our priority is to work with local producers whose quality meets our expectations. If we wouldn't consume it, we won't sell it.",
      category: "product-info",
      relatedQuestions: ["faq-11", "faq-4"]
    },
    {
      id: "faq-9",
      slug: "track-order",
      question: "Can I track my order?",
      answer: "Sorry, but no. We don't currently offer order tracking but it is something we are working on. Stay tuned!",
      category: "delivery",
      relatedQuestions: ["faq-7", "faq-3"]
    },
    {
      id: "faq-10",
      slug: "website-issues",
      question: "I am having issues with your website. Who do I contact?",
      answer: "Please contact us at <a href='mailto:info@716cannabisllc.com' class='text-primary_green hover:underline'>info@716cannabisllc.com</a>. We really appreciate any feedback we get about our website as we strive to constantly improve it for our customers.",
      category: "technical",
      relatedQuestions: []
    },
    {
      id: "faq-11",
      slug: "packaging-recycling",
      question: "How can I recycle order packaging?",
      answer: "We understand the cannabis industry is using far too much plastic. The packaging for each product is different and you should see recycling information on the packaging if it's recyclable.",
      category: "product-info",
      relatedQuestions: ["faq-8"]
    }
  ]
  
  // Group FAQ items by category for easier filtering
  const categoriesMap = {
    "all": "All Questions",
    "order-issues": "Order Issues",
    "delivery": "Delivery",
    "product-info": "Product Information",
    "store-info": "Store Information",
    "technical": "Technical Help"
  }

  return (
    <div className="container mx-auto py-12 px-6">
      {/* Header Section */}
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold mb-4">
          <span className="text-primary_green">FAQ </span>
          <span>Topics</span>
        </h1>
        <div className="max-w-3xl mx-auto">
          <p className="text-lg mb-8">
            Here, we've gathered a comprehensive list of frequently asked questions to provide you with all the
            information you need about our products, services, and shopping experience. Whether you're new to the
            world of cannabis or a seasoned enthusiast, we're here to address your queries and ensure that you have
            a clear understanding of our offerings. Explore this section to find answers regarding product availability,
            consumption guidelines, legal regulations, payment methods, and much more. If you don't find what you're
            looking for, feel free to reach out to our knowledgeable staff who are always ready to assist you.
          </p>
        </div>
      </div>
      
      {/* FAQ Topics Section - Three Column Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
        {/* Order Issues Column */}
        <div className="bg-white p-6 rounded-lg shadow-md">
          <div className="flex items-center mb-4">
            <div className="bg-primary_green/10 p-3 rounded-full mr-3">
              <MapPin className="h-6 w-6 text-primary_green" />
            </div>
            <h3 className="text-xl font-bold">Order Issues</h3>
          </div>
          <div className="border-t border-gray-200 my-4"></div>
          <div className="space-y-3">
            {faqItems
              .filter(item => item.category === "order-issues")
              .slice(0, 3)
              .map(item => (
                <FaqLink 
                  key={item.id}
                  id={item.id}
                  slug={item.slug}
                  question={item.question}
                  navigateToFaq={navigateToFaq}
                />
              ))
            }
          </div>
          <div className="border-t border-gray-200 my-4"></div>
          <button 
            className="text-primary_green flex items-center hover:underline bg-transparent border-0 p-0 cursor-pointer"
            onClick={() => {
              filterByCategory("order-issues");
              // Scroll to the FAQ section
              setTimeout(() => {
                document.getElementById("faq")?.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }, 100);
            }}
          >
            <span>View All Order Issues</span>
            <ChevronRight className="h-4 w-4 ml-1" />
          </button>
        </div>

        {/* Delivery Column */}
        <div className="bg-white p-6 rounded-lg shadow-md">
          <div className="flex items-center mb-4">
            <div className="bg-primary_green/10 p-3 rounded-full mr-3">
              <Box className="h-6 w-6 text-primary_green" />
            </div>
            <h3 className="text-xl font-bold">Delivery</h3>
          </div>
          <div className="border-t border-gray-200 my-4"></div>
          <div className="space-y-3">
            {faqItems
              .filter(item => item.category === "delivery")
              .map(item => (
                <FaqLink 
                  key={item.id}
                  id={item.id}
                  slug={item.slug}
                  question={item.question}
                  navigateToFaq={navigateToFaq}
                />
              ))
            }
          </div>
          <div className="border-t border-gray-200 my-4"></div>
          <button 
            className="text-primary_green flex items-center hover:underline bg-transparent border-0 p-0 cursor-pointer"
            onClick={() => {
              filterByCategory("delivery");
              // Scroll to the FAQ section
              setTimeout(() => {
                document.getElementById("faq")?.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }, 100);
            }}
          >
            <span>View All Delivery Info</span>
            <ChevronRight className="h-4 w-4 ml-1" />
          </button>
        </div>

        {/* Product Info Column */}
        <div className="bg-white p-6 rounded-lg shadow-md">
          <div className="flex items-center mb-4">
            <div className="bg-primary_green/10 p-3 rounded-full mr-3">
              <Settings className="h-6 w-6 text-primary_green" />
            </div>
            <h3 className="text-xl font-bold">Products</h3>
          </div>
          <div className="border-t border-gray-200 my-4"></div>
          <div className="space-y-3">
            {faqItems
              .filter(item => item.category === "product-info")
              .slice(0, 3)
              .map(item => (
                <FaqLink 
                  key={item.id}
                  id={item.id}
                  slug={item.slug}
                  question={item.question}
                  navigateToFaq={navigateToFaq}
                />
              ))
            }
          </div>
          <div className="border-t border-gray-200 my-4"></div>
          <button 
            className="text-primary_green flex items-center hover:underline bg-transparent border-0 p-0 cursor-pointer"
            onClick={() => {
              filterByCategory("product-info");
              // Scroll to the FAQ section
              setTimeout(() => {
                document.getElementById("faq")?.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }, 100);
            }}
          >
            <span>View All Product Info</span>
            <ChevronRight className="h-4 w-4 ml-1" />
          </button>
        </div>
      </div>

      {/* Feature Banner Section */}
      <div className="bg-primary_green text-white p-10 rounded-lg mb-16">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div>
            <h2 className="text-3xl font-bold mb-4">High-Quality Cannabis At Affordable Prices</h2>
            <p className="mb-6">We source only the best in class products to make sure our customers are served the highest quality products available.</p>
          </div>
          <div className="hidden md:block">
            {/* This space can be used for an image if needed */}
          </div>
        </div>
      </div>

      {/* Info Boxes Section */}
      <div className="bg-gray-50 p-8 rounded-lg mb-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white p-6 rounded-lg shadow-sm flex items-center">
            <div className="bg-primary_green/10 p-3 rounded-full mr-3">
              <Tag className="h-6 w-6 text-primary_green" />
            </div>
            <div>
              <h3 className="font-bold">Special Offers</h3>
              <p className="text-sm text-gray-600">Browse current discounts</p>
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-sm flex items-center">
            <div className="bg-primary_green/10 p-3 rounded-full mr-3">
              <MapPin className="h-6 w-6 text-primary_green" />
            </div>
            <div>
              <h3 className="font-bold">Pick Up Points</h3>
              <p className="text-sm text-gray-600">Join our membership club!</p>
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-sm flex items-center">
            <div className="bg-primary_green/10 p-3 rounded-full mr-3">
              <Check className="h-6 w-6 text-primary_green" />
            </div>
            <div>
              <h3 className="font-bold">Priority Support</h3>
              <p className="text-sm text-gray-600">Contact us anytime!</p>
            </div>
          </div>
        </div>
      </div>

      {/* Popular FAQs Section */}
      <div className="mb-16" id="faq">
        <div className="text-center mb-6">
          <h2 className="text-3xl font-bold mb-4">
            <span className="text-primary_green">Popular </span>
            <span>FAQs</span>
          </h2>
          <p className="text-lg max-w-2xl mx-auto">
            Browse our most frequently asked questions to help you understand how we operate.
          </p>
        </div>
        
        {/* Category Filters */}
        <div className="flex flex-wrap justify-center gap-2 mb-8">
          <button 
            onClick={() => filterByCategory('all')}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${activeCategory === 'all' ? 'bg-primary_green text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-800'}`}
          >
            All Questions
          </button>
          {Object.entries(categoriesMap).filter(([key]) => key !== 'all').map(([key, label]) => (
            <button
              key={key}
              onClick={() => filterByCategory(key)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${activeCategory === key ? 'bg-primary_green text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-800'}`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* FAQ Accordion */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* First column */}
          <div className="space-y-4">
            {faqItems
              .filter(item => activeCategory === 'all' || item.category === activeCategory)
              .slice(0, Math.ceil(faqItems.filter(item => activeCategory === 'all' || item.category === activeCategory).length / 2))
              .map((item) => (
                <div 
                  id={item.id} 
                  key={item.id} 
                  className="border border-gray-200 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-200"
                >
                  <button
                    onClick={() => toggleFaqItem(item.id)}
                    className={`flex justify-between items-center w-full p-4 text-left font-medium ${openItemId === item.id ? 'bg-gray-50' : 'bg-white'}`}
                    aria-expanded={openItemId === item.id}
                    aria-controls={`content-${item.id}`}
                  >
                    <span className="text-gray-800">{item.question}</span>
                    {openItemId === item.id ? (
                      <ChevronUp className="h-5 w-5 text-primary_green flex-shrink-0" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-gray-500 flex-shrink-0" />
                    )}
                  </button>
                  
                  {openItemId === item.id && (
                    <div 
                      id={`content-${item.id}`} 
                      className="p-4 bg-white border-t border-gray-200"
                    >
                      <div className="prose prose-sm max-w-none text-gray-600 mb-4" dangerouslySetInnerHTML={{ __html: item.answer }} />
                      
                      {/* Related Questions */}
                      {item.relatedQuestions && item.relatedQuestions.length > 0 && (
                        <div className="mt-4 pt-4 border-t border-gray-100">
                          <p className="text-sm font-medium mb-2">Related Questions:</p>
                          <div className="space-y-2">
                            {item.relatedQuestions.map(relatedId => {
                              const relatedItem = faqItems.find(faq => faq.id === relatedId);
                              if (!relatedItem) return null;
                              
                              return (
                                <button
                                  key={relatedId}
                                  onClick={() => toggleFaqItem(relatedId)}
                                  className="text-primary_green hover:underline text-sm flex items-center w-full text-left"
                                >
                                  <ArrowRight className="h-3 w-3 mr-2 flex-shrink-0" />
                                  {relatedItem.question}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
          </div>
          
          {/* Second column */}
          <div className="space-y-4">
            {faqItems
              .filter(item => activeCategory === 'all' || item.category === activeCategory)
              .slice(Math.ceil(faqItems.filter(item => activeCategory === 'all' || item.category === activeCategory).length / 2))
              .map((item) => (
                <div 
                  id={item.id} 
                  key={item.id} 
                  className="border border-gray-200 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-200"
                >
                  <button
                    onClick={() => toggleFaqItem(item.id)}
                    className={`flex justify-between items-center w-full p-4 text-left font-medium ${openItemId === item.id ? 'bg-gray-50' : 'bg-white'}`}
                    aria-expanded={openItemId === item.id}
                    aria-controls={`content-${item.id}`}
                  >
                    <span className="text-gray-800">{item.question}</span>
                    {openItemId === item.id ? (
                      <ChevronUp className="h-5 w-5 text-primary_green flex-shrink-0" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-gray-500 flex-shrink-0" />
                    )}
                  </button>
                  
                  {openItemId === item.id && (
                    <div 
                      id={`content-${item.id}`} 
                      className="p-4 bg-white border-t border-gray-200"
                    >
                      <div className="prose prose-sm max-w-none text-gray-600 mb-4" dangerouslySetInnerHTML={{ __html: item.answer }} />
                      
                      {/* Related Questions */}
                      {item.relatedQuestions && item.relatedQuestions.length > 0 && (
                        <div className="mt-4 pt-4 border-t border-gray-100">
                          <p className="text-sm font-medium mb-2">Related Questions:</p>
                          <div className="space-y-2">
                            {item.relatedQuestions.map(relatedId => {
                              const relatedItem = faqItems.find(faq => faq.id === relatedId);
                              if (!relatedItem) return null;
                              
                              return (
                                <button
                                  key={relatedId}
                                  onClick={() => toggleFaqItem(relatedId)}
                                  className="text-primary_green hover:underline text-sm flex items-center w-full text-left"
                                >
                                  <ArrowRight className="h-3 w-3 mr-2 flex-shrink-0" />
                                  {relatedItem.question}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
          </div>
        </div>
        
        {/* No results message */}
        {faqItems.filter(item => activeCategory === 'all' || item.category === activeCategory).length === 0 && (
          <div className="text-center p-8 bg-gray-50 rounded-lg">
            <p>No questions found in this category. Try selecting a different category.</p>
          </div>
        )}
      </div>

      {/* Contact Section */}
      <div className="bg-gray-50 p-8 rounded-lg">
        <h2 className="text-2xl font-bold mb-4">Still Have Questions?</h2>
        <p className="mb-6">
          Our team is here to help! Contact us by phone, email, or visit our store for personalized assistance.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white p-6 rounded-lg shadow-sm">
            <h3 className="font-bold mb-2">Call Us</h3>
            <p>+1 (716) 584-3123</p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-sm">
            <h3 className="font-bold mb-2">Email Us</h3>
            <p>info@716cannabisllc.com</p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-sm">
            <h3 className="font-bold mb-2">Visit Us</h3>
            <p>5161 Camp Rd, Hamburg, NY 14075</p>
          </div>
        </div>
      </div>
    </div>
  )
}
